import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OnboardingFormData } from '../types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import useMenuFocusReturn from "../hooks/useMenuFocusReturn"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { User, LogOut, HelpCircle } from 'lucide-react';

interface UserMenuProps {
  formData?: Partial<OnboardingFormData>;
}

const UserMenu: React.FC<UserMenuProps> = ({ formData }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const getDisplayName = () => {
    if (formData?.companyName) return formData.companyName;
    return currentUser?.business_name || '';
  };

  const getContactPerson = () => {
    return formData?.contactPerson || currentUser?.contact_person || '';
  };

  const getEmail = () => {
     return formData?.email || currentUser?.email || '';
  }

  const getInitials = () => {
    const contact = getContactPerson().trim();
    if (contact) {
      const parts = contact.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
    }
    const company = getDisplayName().trim();
    if (company) {
      const parts = company.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return company.substring(0, 2).toUpperCase();
    }
    return '';
  }

  const { triggerProps, onCloseAutoFocus } = useMenuFocusReturn();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex items-center gap-4">
      <span className="hidden md:inline-block font-medium text-sm text-foreground">
        {getDisplayName()}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full hover:bg-transparent p-0" {...triggerProps}>
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-[#0052F4] text-white">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount onCloseAutoFocus={onCloseAutoFocus}>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {getEmail()}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <User className="mr-2 h-4 w-4" />
            <span>Unternehmen</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/help')}>
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>Hilfe</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-cancelled-strong focus:text-cancelled-strong">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Abmelden</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserMenu;
